import { useMemo } from 'react'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'
import { IRoom, RoomData, RoomTypes } from 'types/rooms.types'
import { useAppSelector } from 'app/hooks'
import Room from './Room'
import CustomText from 'components/text/CustomText'
import Separator from 'components/separator/Separator'
import colors from 'utils/colors'
import useRoom from 'utils/hooks/useRoom'

interface IConversationListProps {
  roomsBySearch: RoomData | undefined | string
  onHideConversationPanel?: () => void
  isThreads?: boolean
}

interface IThreadRoomItemProps {
  thread: any
  roomId: string
  roomsBySearch: RoomData | undefined | string
  onNavigateByRoomId: (directRoomId: string) => void
  onHideConversationPanel?: () => void
}


const ThreadRoomItem = ({
  thread,
  roomId,
  roomsBySearch,
  onNavigateByRoomId,
  onHideConversationPanel,
}: IThreadRoomItemProps) => {
  const { myId, users, room } = useRoom({ roomId: thread?.roomId })
  


  const groups = useAppSelector((state) => state.groups.data)
  const channel = groups
  .flatMap((g: any) => g.channels)
  .find((c: any) => c._id === room?._id);

  if (!room) return null

  return (
    <Room
      key={thread._id}
      myId={myId}
      isAppear={true}
      notificationCount={0}
      isSearch={!!roomsBySearch}
      roomId={thread._id}
      currentRoomId={roomId}
      roomOwner={undefined}
      roomType={room.type || 'GM'}
      members={thread.repliedUsers || []}
      name={channel?.name ?? room?.roomName}
      users={users || {}}
      onNavigate={onNavigateByRoomId}
      onHideConversationPanel={onHideConversationPanel}
    />
  )
}

const ConversationList = ({
  roomsBySearch,
  onHideConversationPanel,
  isThreads,
}: IConversationListProps) => {
  const threadList = useAppSelector((state) => state.message?.threadList || [])
  const navigate = useNavigate()
  const { roomId = '', threadId = '' } = useParams()

  const myId = useAppSelector((state) => state.auth.id)
  const otrEnabled = useAppSelector(
    (state) => state.system.data.features?.otrEnabled
  )
  const rooms: IRoom[] = useAppSelector((state) => state.rooms.data)
  const users = useAppSelector((state) => state.users.data)

  const otrRooms = useMemo(() => {
    return rooms.filter((room) => room?.isVanishMode)
  }, [rooms])

  const filtredRooms = useMemo(() => {
    return rooms.filter((room) => room?.type !== 'CM' && room?.type !== 'MM' && !room?.isVanishMode)
  }, [rooms])

  const onNavigateByRoomId = async (directRoomId: string) => {
    navigate(directRoomId)
  }

  const mappingRooms = useMemo(() => {
    return roomsBySearch ?? filtredRooms ?? []
  }, [roomsBySearch, filtredRooms])

  const updatedMappingRooms = useMemo(() => {
    if (!Array.isArray(mappingRooms) || mappingRooms.length === 0) {
      return mappingRooms
    }

    return mappingRooms.map((room: IRoom) => {
      if (room.type === RoomTypes.DM) {
        const updatedRoom = {
          ...room,
          userData: { ...room.userData },
        }

        Object.keys(room.userData || {}).forEach((userId: string) => {
          if (users[userId] && myId !== userId) {
            updatedRoom.userData[userId] = {
              ...room.userData[userId],
              status: users[userId].status,
            }
          }
        })

        return updatedRoom
      }

      return room
    })
  }, [mappingRooms, users, myId])

  if (typeof updatedMappingRooms === 'string') {
    return (
      <Wrapper>
        <NoResultText typography="header3" textAlign="center" color={colors.white}>
          {updatedMappingRooms}
        </NoResultText>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      {otrEnabled && (
        <>
          {otrRooms?.map((room: IRoom) => (
            <Room
              key={room._id}
              isAppear
              isSearch={!!roomsBySearch}
              myId={myId}
              roomId={room._id}
              currentRoomId={roomId}
              roomType={room.type}
              name={room?.name}
              roomOwner={
                room.userData ? room.userData[room.createdByUserId] : undefined
              }
              invitedUsers={[
                ...(room?.invitedUsers || []),
                ...(room?.acceptingUsers || []),
              ]}
              members={room.members || []}
              users={room.userData || {}}
              isVanishMode={room.isVanishMode}
              onNavigate={onNavigateByRoomId}
              onHideConversationPanel={onHideConversationPanel}
              notificationCount={room?.notificationCount}
            />
          ))}

          {otrRooms?.length !== 0 && (
            <Separator margin={{ top: 20, bottom: 10, left: 16, right: 16 }} />
          )}
        </>
      )}

      {Array.isArray(updatedMappingRooms) &&
        !isThreads &&
        updatedMappingRooms.map((room: IRoom) => (
          <Room
            key={room._id}
            myId={myId}
            isAppear={room?.isAppear}
            notificationCount={room?.notificationCount}
            isSearch={!!roomsBySearch}
            roomId={room._id}
            currentRoomId={roomId}
            roomOwner={
              room.userData ? room.userData[room.createdByUserId] : undefined
            }
            roomType={room.type}
            members={room.members}
            name={room?.name || ''}
            users={room.userData || {}}
            onNavigate={onNavigateByRoomId}
            onHideConversationPanel={onHideConversationPanel}
          />
        ))}

      {Array.isArray(threadList) &&
        isThreads &&
        threadList.map((thread: any) => (
          <ThreadRoomItem
            key={thread._id}
            thread={thread}
            roomId={threadId}
            roomsBySearch={roomsBySearch}
            onNavigateByRoomId={onNavigateByRoomId}
            onHideConversationPanel={onHideConversationPanel}
          />
        ))}
    </Wrapper>
  )
}

export default ConversationList

const Wrapper = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  padding: 10px 0px 16px 0px;
`

const NoResultText = styled(CustomText)`
  margin-top: 20px;
`